import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCookieConsent } from '../contexts/CookieConsentContext';

function Toggle({ checked, disabled, onChange, label }) {
  return (
    <label className="flex items-start justify-between gap-4 cursor-pointer">
      <span className="text-sm text-gray-700 leading-relaxed">{label}</span>
      <span className="relative inline-flex h-6 w-11 flex-shrink-0 items-center">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span
          className={`h-6 w-11 rounded-full transition-colors ${
            disabled ? 'bg-gray-300' : checked ? 'bg-primary' : 'bg-gray-300'
          }`}
        />
        <span
          className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </span>
    </label>
  );
}

function CookiePreferencesPanel({ draft, setDraft, onCancel, onSave }) {
  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 p-4 sm:items-center sm:p-6 font-montserrat"
      role="dialog"
      aria-modal="true"
      aria-label="Preferências de cookies"
    >
      <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-2xl">
        <div className="p-5 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900">Preferências de cookies</h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">
            Escolha quais cookies deseja permitir. Os essenciais são necessários para o site funcionar.
          </p>

          <div className="mt-5 space-y-4 rounded-xl border border-gray-100 bg-gray-50 p-4">
            <Toggle
              checked
              disabled
              onChange={() => {}}
              label="Essenciais — necessários para navegação, segurança e preferências básicas."
            />
            <Toggle
              checked={draft.analytics}
              onChange={(checked) => setDraft((prev) => ({ ...prev, analytics: checked }))}
              label="Análise — ajudam a entender como o site é utilizado."
            />
            <Toggle
              checked={draft.marketing}
              onChange={(checked) => setDraft((prev) => ({ ...prev, marketing: checked }))}
              label="Marketing — permitem medir anúncios e campanhas."
            />
          </div>

          <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-full px-5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onSave}
              className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-primary/25 transition-colors hover:bg-primary-hover"
            >
              Salvar preferências
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CookieConsentBanner() {
  const {
    showBanner,
    showPreferences,
    preferences,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    closePreferences,
  } = useCookieConsent();

  const [draft, setDraft] = useState(preferences);

  useEffect(() => {
    if (showBanner || showPreferences) {
      setDraft(preferences);
    }
  }, [showBanner, showPreferences, preferences]);

  if (!showBanner && !showPreferences) return null;

  if (showPreferences) {
    return (
      <CookiePreferencesPanel
        draft={draft}
        setDraft={setDraft}
        onCancel={closePreferences}
        onSave={() => savePreferences(draft)}
      />
    );
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[70] p-4 sm:p-6 font-montserrat pointer-events-none"
      role="dialog"
      aria-live="polite"
      aria-label="Aviso de cookies"
    >
      <div className="pointer-events-auto mx-auto flex max-w-6xl flex-col items-stretch gap-4 rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-[0_8px_30px_rgba(0,0,0,0.12)] sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:px-6 sm:py-5">
        <p className="text-sm leading-relaxed text-gray-700 sm:text-[15px]">
          Usamos cookies pra melhorar sua experiência e medir o desempenho do site. Veja nossa{' '}
          <Link
            to="/politica-de-privacidade"
            className="font-medium text-primary underline underline-offset-2 hover:text-primary-hover"
          >
            Política de Privacidade
          </Link>
          .
        </p>

        <div className="flex shrink-0 items-center justify-end gap-4 sm:gap-5">
          <button
            type="button"
            onClick={rejectNonEssential}
            className="text-sm font-semibold text-gray-800 transition-colors hover:text-gray-950"
          >
            Recusar
          </button>
          <button
            type="button"
            onClick={acceptAll}
            className="rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-primary/30 transition-all hover:bg-primary-hover hover:shadow-primary/40"
          >
            Aceitar tudo
          </button>
        </div>
      </div>
    </div>
  );
}
